import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { KICHI_HOTPOT_SELECT_SOUP } from "utils/routerPath";
import { getKichiConfig, getKichiGroups, getKichiScope, hasKichiInCart, hasKichiOrdered } from "utils/kichiHotpot";

const KichiContext = createContext(null);

// Deliberately kept outside persisted Redux: a reload must evaluate the table again.
export const KichiHotpotProvider = ({ children }) => {
  const homeBrand = useSelector((state) => state.rootReducer.homeBrand);
  const { pathname } = useLocation();
  const scope = getKichiScope(homeBrand);
  const [visits, setVisits] = useState({});
  useEffect(() => setVisits({}), [scope]);
  useEffect(() => {
    if (["/thank-for-visiting", "/qr-order", "/qr-order/static"].includes(pathname)) setVisits({});
  }, [pathname]);
  const updateVisit = useCallback((scope, patch) => {
    setVisits((previous) => ({ ...previous, [scope]: { ...previous[scope], ...patch } }));
  }, []);
  const resetVisits = useCallback(() => setVisits({}), []);
  const value = useMemo(() => ({ visits, updateVisit, resetVisits }), [visits, updateVisit, resetVisits]);
  return <KichiContext.Provider value={value}>{children}</KichiContext.Provider>;
};

const useKichiHotpot = () => {
  const { visits, updateVisit, resetVisits } = useContext(KichiContext);
  const homeBrand = useSelector((state) => state.rootReducer.homeBrand);
  const params = useSelector((state) => state.rootReducer.paramConfig);
  const isPayAdvance = useSelector((state) => state.rootReducer.isPayAdvance);
  const menu = useSelector((state) => state.rootReducer.menu);
  const cart = useSelector((state) => state.rootReducer.cartDetail);
  const scope = getKichiScope(homeBrand);
  const baseConfig = useMemo(() => getKichiConfig(params), [params]);
  // Trả trước chưa hỗ trợ chọn nồi lẩu Kichi (không có đơn/guid): tắt hẳn.
  const config = useMemo(
    () => (isPayAdvance ? { ...baseConfig, enabled: false } : baseConfig),
    [baseConfig, isPayAdvance],
  );
  const groups = useMemo(() => getKichiGroups(menu, config.groupTypes), [menu, config]);
  const visit = visits[scope] || {};
  const inCart = hasKichiInCart(cart, groups, scope);
  const ordered = hasKichiOrdered(visit.order, groups) || !!visit.submitted;
  const markPresented = useCallback(() => updateVisit(scope, { presented: true }), [scope, updateVisit]);
  const recordOrder = useCallback((order) => updateVisit(scope, { order }), [scope, updateVisit]);
  const markSubmitted = useCallback(() => updateVisit(scope, { submitted: true }), [scope, updateVisit]);
  return {
    scope, groups, groupTypes: config.groupTypes, enabled: config.enabled, hasHotpot: inCart || ordered,
    hasHotpotInCart: inCart, hasHotpotOrdered: ordered,
    presented: !!visit.presented, order: visit.order,
    showGroups: config.enabled && groups.length > 0 && !inCart && !ordered,
    markPresented, recordOrder, markSubmitted, resetVisits,
  };
};

export default useKichiHotpot;

// Ordinary menu groups can contain the same broth codes as the Kichi groups.
// Route those entry points through the selector rather than adding individual soups.
export const useKichiHotpotEntry = (product) => {
  const kichi = useKichiHotpot();
  const navigate = useNavigate();
  const group = kichi.groups.find((candidate) =>
    (!product?.kichiGroupType || Number(candidate["group-type"]) === product.kichiGroupType) &&
    candidate.items?.some((item) => String(item.code) === String(product?.code)));
  return useCallback(() => {
    if (!kichi.enabled || !group) return false;
    if (kichi.hasHotpot) navigate(kichi.hasHotpotInCart ? "/cart" : "/ordered");
    else navigate(`${KICHI_HOTPOT_SELECT_SOUP}?groupType=${group["group-type"]}`);
    return true;
  }, [kichi.enabled, kichi.hasHotpot, kichi.hasHotpotInCart, group, navigate]);
};
